import { CheckmarkOrb, CloseOrb } from '@clearkit/icons';
import classnames from 'classnames';
import {
  CKButton,
  CKContainerProps,
  CKLayoutGrid,
  CKLayoutHorizontal,
} from 'clearkit';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AccountPlanEnum,
  useUpgradeAccountPlanMutation,
} from '~/generated/graphql';
import { useCreditUsage } from '~/hooks/account/useCreditUsage';

import { useStatusToast } from '../unified/hooks';
import { UpgradeAccountFormButton } from '../UpgradeAccountFormButton';

interface HypeBoxProps {
  children: ReactNode;
  variant: 'green' | 'purple' | 'blue';
  className?: string;
}

interface DescriptionProps {
  children: ReactNode;
}

interface FeatureProps {
  status: 'available' | 'unavailable';
  children: ReactNode;
}

const ColumnWrapper = ({ className, children }: CKContainerProps) => (
  <CKLayoutGrid
    className={classnames('h-full', className)}
    gapRow="1.5rem"
    gridTemplateRows="minmax(16rem, 1fr) 1fr auto"
  >
    {children}
  </CKLayoutGrid>
);

const ColumnWrapperHeader = ({ children }: CKContainerProps) => (
  <CKLayoutGrid
    as="header"
    className="pb-6 border-b border-gray-100"
    gap="2rem"
    gridTemplateRows="auto auto 1fr"
  >
    {children}
  </CKLayoutGrid>
);

const FeatureList = ({ children }: CKContainerProps) => (
  <div className="pb-4 text-sm font-medium space-y-4">{children}</div>
);

const Feature: React.FC<FeatureProps> = ({ status, children }) => {
  const isAvailable = status === 'available';
  const icon = isAvailable ? (
    <CheckmarkOrb className="fill-gradient-br-green" />
  ) : (
    <CloseOrb className="fill-gradient-br-red" />
  );
  const textStyle = classnames({
    'text-gray-400 line-through': !isAvailable,
  });

  return (
    <CKLayoutHorizontal gap="0.5rem" justifyContent="start" wrap="nowrap">
      {icon}
      <span className={textStyle}>{children}</span>
    </CKLayoutHorizontal>
  );
};

const HypeBox = ({ children, variant, className }: HypeBoxProps) => {
  /**
   * @todo add text gradient utility classes like we have for icon fills
   */
  const gradients = {
    green: 'from-green-300 to-green-500',
    purple: 'from-purple-500 to-blue-500',
    blue: 'from-blue-300 to-blue-500',
  };

  return (
    <div>
      <span
        className={classnames(
          'inline-block leading-none p-4 border text-xl rounded-lg font-semibold overflow-hidden',
          className,
          {
            [`bg-${variant}-0 border-${variant}-300`]: variant,
          },
        )}
      >
        <span
          className={classnames(
            'text-transparent bg-clip-text bg-gradient-to-br',
            gradients[variant],
          )}
        >
          {children}
        </span>
      </span>
    </div>
  );
};

const Description = ({ children }: DescriptionProps) => {
  return <p className="text-gray-600">{children}</p>;
};

export const FreePlan = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isHubspotAccount, isFreeAccount } = useCreditUsage();
  const { addSuccessToast } = useStatusToast();

  const [upgradeAccountPlan] = useUpgradeAccountPlanMutation({
    onCompleted: () => {
      setIsSubmitting(false);
      addSuccessToast({
        description:
          'Your subscription limit will be updated when your invoice has been successfully processed. You will recieve an email confirmation shortly.',
        heading: 'Subscription Updated',
      });
    },
  });

  return (
    <ColumnWrapper>
      <ColumnWrapperHeader>
        <HypeBox variant="green">Free</HypeBox>

        <Description>Free now, free forever.</Description>

        <h3 className="self-end text-3xl font-bold">
          <span className="relative text-sm bottom-2">$</span>
          <span className="">0</span>
        </h3>
      </ColumnWrapperHeader>

      <FeatureList>
        {isHubspotAccount ? (
          <>
            <Feature status="available">500 credits each month</Feature>
            <Feature status="available">
              Discover more leads, better leads
            </Feature>
            <Feature status="available">
              Reveal visitors on your site in your market
            </Feature>
            <Feature status="available">
              Build prospect lists & target account lists directly in HubSpot
            </Feature>
          </>
        ) : (
          <>
            <Feature status="available">Prospect qualified contacts</Feature>
            <Feature status="available">Form Shortening</Feature>
            <Feature status="available">
              Batch data enrichments and exports
            </Feature>
            <Feature status="available">Alerts</Feature>
            <Feature status="unavailable">Automated CRM Enrichment</Feature>
            <Feature status="unavailable">Enable Single Sign On (SSO)</Feature>
            <Feature status="unavailable">Access Enterprise Controls</Feature>
          </>
        )}
      </FeatureList>

      <CKButton
        className="w-full"
        isDisabled={isFreeAccount}
        isLoading={isSubmitting}
        onClick={() => {
          upgradeAccountPlan({
            variables: {
              input: {
                planId: isHubspotAccount
                  ? AccountPlanEnum.HubspotFreeTier
                  : AccountPlanEnum.FreeTier,
              },
            },
          });
        }}
        variant="simple"
        variantColor="default"
      >
        {isFreeAccount ? 'Current Plan' : 'Downgrade to Free Tier'}
      </CKButton>
    </ColumnWrapper>
  );
};

export const GrowthPlan = ({ url }: { url?: string }) => {
  const { isHubspotAccount, isPaidAccount } = useCreditUsage();

  return (
    <ColumnWrapper>
      <ColumnWrapperHeader>
        <HypeBox variant="blue">Growth</HypeBox>

        <Description>
          More credits, self-serve, month-to-month. Just bring your credit card.
        </Description>

        <div className="self-end">
          <p className="mb-2">Options from</p>
          <div className="flex items-center text-gray-900 space-x-2">
            <span className="font-bold">
              <span className="relative bottom-2">$</span>
              <span className="text-3xl">{299}</span>
            </span>
            <span className="relative top-0.5">to</span>
            <span className="font-bold">
              <span className="relative bottom-2">$</span>
              <span className="text-3xl">{749}</span>
            </span>
            <span className="relative top-0.5">per month</span>
          </div>
        </div>
      </ColumnWrapperHeader>

      <FeatureList>
        {isHubspotAccount ? (
          <>
            <Feature status="available">
              Credit tiers from 1,000 to 2,000 credits each month
            </Feature>
            <Feature status="available">
              Discover more leads, better leads
            </Feature>
            <Feature status="available">
              Reveal visitors on your site in your market
            </Feature>
            <Feature status="available">
              Build prospect lists & target account lists directly in HubSpot
            </Feature>
          </>
        ) : (
          <>
            <Feature status="available">Prospect qualified contacts</Feature>
            <Feature status="available">Form Shortening</Feature>
            <Feature status="available">
              Batch data enrichments and exports
            </Feature>
            <Feature status="available">Alerts</Feature>
            <Feature status="unavailable">Automated CRM Enrichment</Feature>
            <Feature status="unavailable">Enable Single Sign On (SSO)</Feature>
            <Feature status="unavailable">Access Enterprise Controls</Feature>
          </>
        )}
      </FeatureList>

      {url &&
        (isPaidAccount ? (
          <CKButton
            as={Link}
            className="w-full text-center"
            to={url}
            variant="simple"
            variantColor="green"
          >
            Update your credit tier
          </CKButton>
        ) : (
          <CKButton
            as={Link}
            className="w-full text-center"
            to={url}
            variant="bold"
            variantColor="green"
          >
            Get Started
          </CKButton>
        ))}
    </ColumnWrapper>
  );
};

export const BusinessPlan = () => {
  return (
    <ColumnWrapper>
      <ColumnWrapperHeader>
        <HypeBox variant="purple">Business</HypeBox>

        <Description>
          For businesses and organizations that need more.
        </Description>

        <h3 className="self-end text-3xl font-bold">Get in touch</h3>
      </ColumnWrapperHeader>

      <FeatureList>
        <Feature status="available">Prospect qualified contacts</Feature>
        <Feature status="available">Form Shortening</Feature>
        <Feature status="available">Batch data enrichments and exports</Feature>
        <Feature status="available">Alerts</Feature>
        <Feature status="available">Automated CRM Enrichment</Feature>
        <Feature status="available">Enable Single Sign On (SSO)</Feature>
        <Feature status="available">Access Enterprise Controls</Feature>
      </FeatureList>

      <UpgradeAccountFormButton
        className="w-full"
        variant="bold"
        variantColor="green"
      >
        Contact Us
      </UpgradeAccountFormButton>
    </ColumnWrapper>
  );
};
